/**
 * The sales pipeline: every prospect, where it stands, and what is due today.
 *
 * The strip above the list describes the whole pipeline whatever the list is filtered to, the way
 * the Invoices page's receivables strip does. The list sorts follow-ups that are due first - they are
 * the work for today - then the soonest follow-up, then the most recently touched.
 */
import type { Container } from 'webwin';
import * as crm from '../api/crm.js';
import { describeError } from '../api/http.js';
import * as marketing from '../api/marketing.js';
import type { LeadDto, LeadListDto, UserDto } from '../contracts.gen.js';
import { join, money, percent, plural } from '../format.js';
import { followUpLine, isOpen, LEAD_SOURCES, LEAD_STATUSES, leadName, sourceLabel, statusLabel, statusTone } from '../leads.js';
import { todayIso } from '../recurrence.js';
import type { Card } from '../ui/Card.js';
import { CardList } from '../ui/CardList.js';
import { chip } from '../ui/Chip.js';
import { EmptyState } from '../ui/EmptyState.js';
import { stat } from '../ui/Stat.js';
import { StatusLine } from '../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  checkBox,
  comboBox,
  field,
  fillWith,
  FONT,
  GAP,
  h1,
  inlineRow,
  muted,
  relayout,
  scrollPage,
  splitRow,
  stackOf,
  text,
  textBox,
  value,
  vstack
} from '../ui/layout.js';
import { leadEditor } from './sections/lead-editor.js';
import type { CrmPage, PageContext } from './page.js';

/** Index 0 is "open leads", 1 "every lead"; the rest are the statuses in pipeline order. */
const STATUS_FILTERS = ['Open leads', 'Every lead', ...LEAD_STATUSES.map(statusLabel)];

export function leadsPage(): CrmPage {
  return {
    async mount(host: Container, ctx: PageContext) {
      let users: UserDto[] = [];
      let loading = false;
      const status = new StatusLine();

      const titleRow = splitRow({ align: 'center' });
      fillWith(titleRow, h1('Leads'));
      titleRow.controls.add(button('New lead', () => editor.open(), true));
      const header = stackOf(
        vstack({ spacing: GAP.xs }),
        titleRow,
        muted('Prospective customers, from first contact to signed. Converting a won lead creates the customer.')
      );

      // ---- pipeline strip ----
      const openStat = stat('Open pipeline a month');
      const dueStat = stat('Follow-ups due');
      const winStat = stat('Win rate');
      const newStat = stat('New this month');
      const stageRow = buttonRow({ spacing: GAP.sm });
      const strip = card({ visible: false });
      strip.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          stackOf(buttonRow({ spacing: GAP.xl, lineSpacing: GAP.md, align: 'start' }), openStat.panel, dueStat.panel, winStat.panel, newStat.panel),
          stageRow
        )
      );

      // ---- filters ----
      const statusFilter = comboBox<string>(STATUS_FILTERS, { selectedIndex: 0, onSelectionChanged: () => void refresh() });
      const sourceFilter = comboBox<string>(['Any source', ...LEAD_SOURCES.map(sourceLabel)], {
        selectedIndex: 0,
        onSelectionChanged: () => void refresh()
      });
      const ownerFilter = comboBox<string>(['Anyone'], { selectedIndex: 0, onSelectionChanged: () => void refresh() });
      const search = textBox({ placeholder: 'Name, email, phone, tag' });
      search.on('keydown', (e: { key: string }) => {
        if (e.key === 'Enter') void refresh();
      });
      const dueOnly = checkBox('Follow-ups due only', ctx.query.get('due') === '1');
      dueOnly.on('change', () => void refresh());
      const countLine = text('');

      const filters = card();
      filters.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          stackOf(
            buttonRow({ spacing: GAP.md, align: 'end' }),
            field('Show', statusFilter, { width: 160 }),
            field('Source', sourceFilter, { width: 170 }),
            field('Owner', ownerFilter, { width: 170 }),
            field('Search', search, { width: 210 }),
            dueOnly
          ),
          stackOf(buttonRow(), button('Apply', () => void refresh(), true), button('Reset', () => reset())),
          countLine
        )
      );

      const editor = leadEditor({
        cancelled: () => ctx.cancelled(),
        onSaved: (lead, created) => {
          if (created) ctx.go(`/leads/${lead.id}`);
          else void refresh();
        }
      });

      const list = new CardList<LeadDto>({ key: l => l.id, build: l => leadCard(l) });
      const empty = new EmptyState('No leads here yet. Add one by hand, or switch on the website form under Marketing.');

      const { view, content } = scrollPage();
      content.controls.add(
        stackOf(vstack({ spacing: GAP.lg }), header, status.label, editor.card, strip, filters, empty.panel, list.panel)
      );
      host.controls.add(view);

      function leadCard(l: LeadDto): Card {
        const heading = inlineRow();
        heading.controls.add(value(leadName(l), { font: FONT.bold }));
        heading.controls.add(chip(l.statusLabel, statusTone(l.status)));
        if (l.unsubscribed) heading.controls.add(chip('unsubscribed', 'warn'));
        for (const tag of l.tags.slice(0, 4)) heading.controls.add(chip(tag, 'neutral'));

        const body = stackOf(vstack({ spacing: GAP.xs }), heading);
        const who = join([l.company ? l.contactName : null, l.email, l.phone]);
        if (who) body.controls.add(muted(who));
        body.controls.add(
          text(
            join([
              l.estimatedMonthlyValue > 0 ? `${money(l.estimatedMonthlyValue)} a month` : null,
              l.sourceLabel,
              l.ownerName ? `owner ${l.ownerName}` : null,
              l.customerName ? `customer ${l.customerName}` : null
            ]),
            { font: FONT.small }
          )
        );
        const follow = isOpen(l.status) ? followUpLine(l.nextFollowUpUtc, todayIso()) : null;
        if (follow) body.controls.add(muted(follow, l.isFollowUpDue ? 'warn' : 'muted'));

        const shell = card({ cursor: 'pointer' });
        shell.controls.add(body);
        shell.onActivate(() => ctx.go(`/leads/${l.id}`));
        return shell;
      }

      function render(data: LeadListDto): void {
        const p = data.pipeline;
        openStat.set(money(p.openMonthlyValue), plural(p.openCount, 'open lead'));
        dueStat.set(String(p.followUpsDue), p.followUpsDue > 0 ? 'due today or overdue' : 'nothing overdue', p.followUpsDue > 0 ? 'warn' : 'text');
        winStat.set(p.wonCount + p.lostCount > 0 ? percent(p.winRatePercent) : '-', `${p.wonCount} won, ${p.lostCount} lost`);
        newStat.set(String(p.newThisMonth));
        stageRow.controls.clear();
        for (const stage of p.stages) {
          const label = join([`${stage.label} ${stage.count}`, stage.monthlyValue > 0 ? money(stage.monthlyValue) : null]);
          stageRow.controls.add(chip(label, statusTone(stage.status)));
        }
        strip.visible = true;

        list.set(data.leads);
        countLine.text =
          data.leads.length === 0 ? '' : `${plural(data.leads.length, 'lead')} shown.`;
        empty.update(data.leads.length);
        relayout(content);
      }

      function filter(): marketing.LeadFilter {
        const i = statusFilter.selectedIndex;
        const owner = ownerFilter.selectedIndex > 0 ? users[ownerFilter.selectedIndex - 1] : undefined;
        return {
          status: i >= 2 ? LEAD_STATUSES[i - 2] : undefined,
          includeClosed: i === 1,
          source: sourceFilter.selectedIndex > 0 ? LEAD_SOURCES[sourceFilter.selectedIndex - 1] : undefined,
          ownerUserId: owner?.id,
          search: search.text,
          followUpDue: dueOnly.checked
        };
      }

      async function refresh(): Promise<void> {
        if (loading) return;
        loading = true;
        try {
          const data = await marketing.getLeads(filter(), ctx.dispose.signal());
          if (ctx.cancelled()) return;
          render(data);
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        } finally {
          loading = false;
        }
      }

      function reset(): void {
        statusFilter.selectedIndex = 0;
        sourceFilter.selectedIndex = 0;
        ownerFilter.selectedIndex = 0;
        search.text = '';
        dueOnly.checked = false;
        void refresh();
      }

      try {
        users = (await crm.getStaffUsers(ctx.dispose.signal())).filter(u => !u.disabled);
        if (ctx.cancelled()) return;
        editor.setUsers(users);
        ownerFilter.items = ['Anyone', ...users.map(u => u.displayName)];
      } catch {
        // The owner pickers stay at "Anyone"/"Nobody"; the list still loads.
      }
      await refresh();
    }
  };
}
